// state/use-reduced-motion.ts — reduced-motion preference (build-contract §D).
//
// Returns true when animations should be suppressed. Honours BOTH:
//   1) the OS `prefers-reduced-motion: reduce` media query, AND
//   2) the in-app accessibility toggle (a11y.reducedMotion from ThemeProvider).
// Either one being on => motion is reduced. Thread this into animated components
// (orb breathe, speak pulse, answer flash, mastery-cell pulse, fade-in).

import { useEffect, useState } from 'react';
import { useTheme } from './theme';

/** True when the OS prefers reduced motion. SSR/jsdom-safe (defaults false). */
export function useOsReducedMotion(): boolean {
  const [osReduced, setOsReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setOsReduced(e.matches);
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', handler);
    else                                            mq.addListener(handler);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', handler);
      else                                              mq.removeListener(handler);
    };
  }, []);

  return osReduced;
}

/** True when motion should be reduced — OS preference OR the in-app toggle. */
export function useReducedMotion(): boolean {
  const { a11y } = useTheme();
  const osReduced = useOsReducedMotion();
  return osReduced || a11y.reducedMotion;
}
